import { useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Alignment, Switch, Tab, Tabs } from "@blueprintjs/core";

import { Feature } from "@utils";

import { ResultsPanel } from "@pages/editor/components/ResultsPanel/ResultsPanel";
import { TargetEditor } from "@pages/editor/components/TargetEditor/TargetEditor";
import { WorkbenchContext } from "@pages/editor/providers";

import { MetadataPanel } from "./MetadataPanel";
import { SemgrepDocsEditorPanel } from "./SemgrepDocsEditorPanel";

const TestPanel = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) min-content;
`;

// 132px is the height of the tab bar + height of footer
const SecondaryTestPanel = styled.div`
  max-height: calc(100vh - 132px);
  overflow-y: scroll;
`;

const TestPanelContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: min-content minmax(0, 1fr);
  max-height: 100%;
`;

/**
 * TODO: This is a hack to work clean with blueprint.js. Will need to update
 * this in the future when we rework the UI.
 */
const TopRightSwitch = styled.div`
  position: absolute;
  display: flex;
  align-content: center;
  top: 5px;
  right: 5px;
  font-size: 13px;
`;

type TAB_ID = "code" | "metadata" | "languages" | "docs";

const Panel = () => {
  const { workbench } = useContext(WorkbenchContext);
  const { bundle, org } = workbench;
  const [selectedTabIndex, setSelectedTabIndex] = useState<TAB_ID>("code");
  if (bundle === null) return null;

  const { isDeep } = bundle;

  // using a `styled(Tab)` seems to confuse blueprint
  const tabStyle = { fontSize: "13px" };

  return (
    <TestPanelContainer>
      <Tabs
        className="bp-header-styled"
        defaultSelectedTabId="primary"
        selectedTabId={selectedTabIndex}
        onChange={(t: TAB_ID) => setSelectedTabIndex(t)}
      >
        <Tab style={tabStyle} id="code" title="test code" />
        <Tab style={tabStyle} id="metadata" title="metadata" />
        <Tab style={tabStyle} id="docs" title="docs" />
      </Tabs>

      {Feature.hasDeeperRun({ org }) && (
        /**
         * Enable a switcher from the FOSS to DEEP runner, only if
         * the user has the `hasDeeperRun` feature flag.
         */
        <TopRightSwitch>
          <div>
            <a href="/deep-semgrep-beta">DeepSemgrep</a>
          </div>
          <Switch
            style={{ margin: "0px" }}
            checked={isDeep}
            alignIndicator={Alignment.RIGHT}
            onChange={bundle.toggleDeep}
          />
        </TopRightSwitch>
      )}

      {selectedTabIndex === "code" && (
        <TestPanel>
          <TargetEditor />
          <ResultsPanel />
        </TestPanel>
      )}
      {selectedTabIndex === "metadata" && (
        <SecondaryTestPanel>
          <MetadataPanel workbench={workbench} />
        </SecondaryTestPanel>
      )}
      {selectedTabIndex === "docs" && (
        <SecondaryTestPanel>
          <SemgrepDocsEditorPanel bundle={bundle} />
        </SecondaryTestPanel>
      )}
    </TestPanelContainer>
  );
};

export const TargetEditorPanel = observer(Panel);
